import type { Point2 } from './point';

/**
 * Convex hull, counter-clockwise, by Andrew's monotone chain.
 *
 * O(n log n) and entirely arithmetic — no trigonometry, so it cannot drift on the
 * degenerate inputs drawings are full of (repeated points, perfectly straight
 * runs).
 */
export const convexHull = (points: readonly Point2[]): Point2[] => {
  if (points.length < 3) {
    return [...points];
  }

  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  // > 0 when the turn is counter-clockwise.
  const cross = (o: Point2, a: Point2, b: Point2) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const build = (ordered: Point2[]): Point2[] => {
    const chain: Point2[] = [];
    ordered.forEach((point) => {
      while (
        chain.length >= 2 &&
        cross(chain[chain.length - 2], chain[chain.length - 1], point) <= 0
      ) {
        chain.pop();
      }
      chain.push(point);
    });
    chain.pop();
    return chain;
  };

  const lower = build(sorted);
  const upper = build([...sorted].reverse());
  const hull = [...lower, ...upper];

  return hull.length >= 3 ? hull : [...sorted];
};

export type OrientedRect = {
  /** The four corners, in order. */
  readonly corners: readonly [Point2, Point2, Point2, Point2];
  /** Side lengths, longer first. */
  readonly width: number;
  readonly height: number;
  readonly area: number;
};

/**
 * Smallest-area rectangle enclosing the points, by rotating calipers.
 *
 * Rests on the theorem that a minimal-area enclosing rectangle always has a side
 * flush with an edge of the convex hull — so only as many orientations as the hull
 * has edges need testing, rather than a search over angles.
 *
 * Returns `null` for fewer than three distinct points, which have no meaningful
 * enclosing rectangle.
 */
export const minAreaRect = (points: readonly Point2[]): OrientedRect | null => {
  const hull = convexHull(points);
  if (hull.length < 3) {
    return null;
  }

  let best: OrientedRect | null = null;

  for (let index = 0; index < hull.length; index += 1) {
    const a = hull[index];
    const b = hull[(index + 1) % hull.length];

    const edgeX = b[0] - a[0];
    const edgeY = b[1] - a[1];
    const edgeLength = Math.hypot(edgeX, edgeY);
    if (edgeLength === 0) {
      continue;
    }

    // Unit axes for this candidate orientation.
    const ux = edgeX / edgeLength;
    const uy = edgeY / edgeLength;
    const vx = -uy;
    const vy = ux;

    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;

    hull.forEach(([x, y]) => {
      const u = x * ux + y * uy;
      const v = x * vx + y * vy;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    });

    const extentU = maxU - minU;
    const extentV = maxV - minV;
    const area = extentU * extentV;

    if (best !== null && area >= best.area) {
      continue;
    }

    // Back from (u, v) coordinates into world space.
    const at = (u: number, v: number): Point2 => [
      u * ux + v * vx,
      u * uy + v * vy,
    ];

    best = {
      corners: [at(minU, minV), at(maxU, minV), at(maxU, maxV), at(minU, maxV)],
      width: Math.max(extentU, extentV),
      height: Math.min(extentU, extentV),
      area,
    };
  }

  return best;
};
